import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, Grid, Alert, Snackbar,
  CircularProgress, Skeleton, InputAdornment, Chip, Tooltip, Divider,
  Autocomplete,
} from '@mui/material';
import { Plus, Search as SearchIcon, Eye, Trash2, RefreshCw, Package, Upload, Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import { getGRNs, addGRN, updateGRN, deleteGRN, generateGRNNumber } from '../firebase/grnService';
import { getInventoryItems } from '../firebase/inventoryService';
import { getPendingPurchaseOrders, linkGRNtoPO } from '../firebase/purchaseOrderService';
import { createTradePayable, deletePayableByGRN } from '../firebase/tradePayablesService';
import { getSuppliers, createSupplier } from '../firebase/supplierService';
import { logActivity, ACTIONS, ENTITY_TYPES } from '../firebase/activityLogService';

const emptyItem = () => ({ name: '', category: '', itemCode: '', qtyReceived: '', costPerUnit: '', isInternal: false });

const fmt = (n) =>
  parseFloat(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const GRN = ({ currentUser, permissions }) => {
  const isAdmin = permissions?.userType === 'admin';
  const canDelete = isAdmin || permissions?.deleteGRN === true;
  const [grns, setGRNs] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [pendingPOs, setPendingPOs] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedPO, setSelectedPO] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingGRN, setEditingGRN] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Quick-add supplier
  const [showQuickSupplier, setShowQuickSupplier] = useState(false);
  const [quickSupplierName, setQuickSupplierName] = useState('');
  const [quickSupplierPhone, setQuickSupplierPhone] = useState('');
  const [quickSupplierEmail, setQuickSupplierEmail] = useState('');
  const [quickSupplierSubmitting, setQuickSupplierSubmitting] = useState(false);

  // Import state
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importPreview, setImportPreview] = useState([]);
  const [importErrors, setImportErrors] = useState([]);
  const fileInputRef = useRef(null);

  const [supplier, setSupplier] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState([emptyItem()]);

  const loadGRNs = useCallback(async () => {
    setLoading(true);
    const [grnResult, invResult, poResult, suppResult] = await Promise.all([
      getGRNs(),
      getInventoryItems(),
      getPendingPurchaseOrders(),
      getSuppliers({ status: 'active' }),
    ]);
    if (grnResult.success) setGRNs(grnResult.data);
    else showSnackbar(grnResult.error || 'Failed to load GRNs', 'error');
    if (invResult.success) setInventory(invResult.data);
    if (poResult.success) setPendingPOs(poResult.data);
    if (suppResult.success) setSuppliers(suppResult.data);
    setLoading(false);
  }, []);

  // Derive unique item names and categories from existing inventory AND past GRNs
  const existingItemNames = useMemo(() => {
    const fromInventory = inventory.map((i) => i.name);
    const fromGRNs = grns.flatMap((g) => g.items?.map((i) => i.name) || []);
    return [...new Set([...fromInventory, ...fromGRNs].filter(Boolean))].sort();
  }, [inventory, grns]);

  const existingCategories = useMemo(() => {
    const fromInventory = inventory.map((i) => i.category);
    const fromGRNs = grns.flatMap((g) => g.items?.map((i) => i.category) || []);
    return [...new Set([...fromInventory, ...fromGRNs].filter(Boolean))].sort();
  }, [inventory, grns]);

  // Derive item codes with their associated product names for autocomplete
  const existingItemCodes = useMemo(() => {
    const fromInventory = inventory
      .filter(i => i.itemCode)
      .map(i => ({ code: i.itemCode, name: i.name, category: i.category || '' }));
    const fromGRNs = grns
      .flatMap(g => g.items || [])
      .filter(i => i.itemCode)
      .map(i => ({ code: i.itemCode, name: i.name, category: i.category || '' }));
    // Deduplicate by code
    const seen = new Set();
    return [...fromInventory, ...fromGRNs].filter(i => {
      if (seen.has(i.code)) return false;
      seen.add(i.code);
      return true;
    }).sort((a, b) => a.code.localeCompare(b.code));
  }, [inventory, grns]);

  useEffect(() => { loadGRNs(); }, [loadGRNs]);

  const showSnackbar = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  const handleQuickAddSupplier = async () => {
    if (!quickSupplierName.trim()) return;
    setQuickSupplierSubmitting(true);
    const result = await createSupplier({
      name: quickSupplierName.trim(),
      phone: quickSupplierPhone.trim(),
      email: quickSupplierEmail.trim(),
      contactPerson: '',
      address: '',
      paymentTerms: 'Net 30',
      taxId: '',
    }, currentUser);
    setQuickSupplierSubmitting(false);
    if (result.success) {
      const newSupplier = { id: result.id, name: quickSupplierName.trim() };
      setSuppliers(prev => [...prev, newSupplier]);
      setSupplier(quickSupplierName.trim());
      setShowQuickSupplier(false);
      setQuickSupplierName('');
      setQuickSupplierPhone('');
      setQuickSupplierEmail('');
      showSnackbar(`Supplier "${quickSupplierName.trim()}" added`);
    } else {
      showSnackbar(result.error || 'Failed to add supplier', 'error');
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadGRNs();
    setRefreshing(false);
    showSnackbar('Refreshed');
  };

  const resetForm = () => {
    setSupplier('');
    setInvoiceNumber('');
    setDate(new Date().toISOString().split('T')[0]);
    setNotes('');
    setItems([emptyItem()]);
    setSelectedPO(null);
  };

  const openCreate = () => {
    resetForm();
    setEditingGRN(null);
    setShowModal(true);
  };

  const openEdit = (grn) => {
    setEditingGRN(grn);
    setSupplier(grn.supplier);
    setInvoiceNumber(grn.invoiceNumber || '');
    setDate(grn.date);
    setNotes(grn.notes || '');
    setItems(grn.items.map((i) => ({ ...i })));
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingGRN(null);
  };

  const updateItem = (index, field, value) =>
    setItems((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });

  const addItem = () => setItems((prev) => [...prev, emptyItem()]);
  const removeItem = (index) => setItems((prev) => prev.filter((_, i) => i !== index));

  // Computed totals
  const itemTotals = items.map((i) => {
    const qty = parseFloat(i.qtyReceived) || 0;
    const cost = parseFloat(i.costPerUnit) || 0;
    return qty * cost;
  });
  const grandTotal = itemTotals.reduce((s, t) => s + t, 0);

  // Download template
  const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      ['Item Name', 'Item Code', 'Category', 'Qty Received', 'Cost Per Unit (LKR)'],
      ['Rose Oud 50ml', 'PRF-001', 'Perfume', 10, 1200.00],
      ['Musk Attar', 'ATT-001', 'Attar', 5, 850.00],
    ]);
    ws['!cols'] = [{ wch: 30 }, { wch: 15 }, { wch: 20 }, { wch: 15 }, { wch: 20 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'GRN Items');
    XLSX.writeFile(wb, 'GRN_Import_Template.xlsx');
  };

  // Parse uploaded file
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = ''; // reset so same file can be re-uploaded

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: 'binary' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });

        if (rows.length < 2) {
          showSnackbar('File is empty or has no data rows', 'error');
          return;
        }

        // Normalise header row — case-insensitive, trim whitespace
        const headers = rows[0].map((h) => String(h).trim().toLowerCase());

        // Map column indices
        const col = (names) => {
          for (const n of names) {
            const idx = headers.findIndex((h) => h.includes(n));
            if (idx !== -1) return idx;
          }
          return -1;
        };

        const nameCol = col(['item name', 'name', 'item', 'product']);
        const catCol  = col(['category', 'cat']);
        const qtyCol  = col(['qty', 'quantity', 'received']);
        const costCol = col(['cost', 'price', 'unit']);
        const codeCol = col(['item code', 'itemcode', 'code', 'sku']);

        if (nameCol === -1 || qtyCol === -1 || costCol === -1) {
          showSnackbar('Could not find required columns. Download the template to see the correct format.', 'error');
          return;
        }

        const preview = [];
        const errors = [];

        rows.slice(1).forEach((row, i) => {
          const rowNum = i + 2;
          const name = String(row[nameCol] || '').trim();
          const category = catCol !== -1 ? String(row[catCol] || '').trim() : '';
          const qty = parseFloat(row[qtyCol]);
          const cost = parseFloat(row[costCol]);

          // Skip completely empty rows silently
          if (!name && isNaN(qty) && isNaN(cost)) return;

          if (!name) { errors.push(`Row ${rowNum}: Item name is empty`); return; }
          if (isNaN(qty) || qty <= 0) { errors.push(`Row ${rowNum}: Invalid quantity "${row[qtyCol]}"`); return; }
          if (isNaN(cost) || cost < 0) { errors.push(`Row ${rowNum}: Invalid cost "${row[costCol]}"`); return; }

          preview.push({ name, category: category || 'Uncategorized', itemCode: codeCol !== -1 ? String(row[codeCol] || '').trim() : '', qtyReceived: qty, costPerUnit: cost });
        });

        setImportPreview(preview);
        setImportErrors(errors);
        setShowImportDialog(true);
      } catch (err) {
        showSnackbar('Failed to read file. Make sure it is a valid Excel or CSV file.', 'error');
      }
    };
    reader.readAsBinaryString(file);
  };

  // Apply imported rows to the items table
  const applyImport = () => {
    if (importPreview.length === 0) return;
    setItems(importPreview.map((row) => ({
      name: row.name,
      category: row.category,
      itemCode: row.itemCode || '',
      qtyReceived: String(row.qtyReceived),
      costPerUnit: String(row.costPerUnit),
    })));
    setShowImportDialog(false);
    setImportPreview([]);
    setImportErrors([]);
    showSnackbar(`${importPreview.length} item(s) imported successfully`);
  };

  const handleSave = async () => {
    if (submitting) return;

    if (!supplier.trim()) {
      showSnackbar('Supplier is required', 'error');
      return;
    }
    if (!invoiceNumber.trim()) {
      showSnackbar('Invoice number is required', 'error');
      return;
    }
    const invalid = items.filter((i) => !i.name.trim() || !i.qtyReceived || !i.costPerUnit);
    if (invalid.length > 0 || items.length === 0) {
      showSnackbar('Fill in item name, qty received and cost per unit for all rows', 'error');
      return;
    }

    // Check for duplicate item codes (non-empty codes must be unique across inventory)
    const codesInThisGRN = items.map(i => i.itemCode?.trim()).filter(Boolean);
    const duplicateInGRN = codesInThisGRN.filter((code, idx) => codesInThisGRN.indexOf(code) !== idx);
    if (duplicateInGRN.length > 0) {
      showSnackbar(`Duplicate item code in this GRN: ${duplicateInGRN[0]}`, 'error');
      return;
    }
    // Only block if the item code belongs to a DIFFERENT product name
    // Same product being restocked with same item code is perfectly valid
    const conflictingCodes = codesInThisGRN.filter(code => {
      const grnItem = items.find(i => i.itemCode?.trim() === code);
      return inventory.some(inv =>
        inv.itemCode === code &&
        inv.name.toLowerCase().trim() !== grnItem?.name?.toLowerCase().trim()
      );
    });
    if (conflictingCodes.length > 0) {
      const conflictItem = items.find(i => i.itemCode?.trim() === conflictingCodes[0]);
      const existingItem = inventory.find(inv => inv.itemCode === conflictingCodes[0]);
      showSnackbar(`Item code "${conflictingCodes[0]}" is already assigned to "${existingItem?.name}". Use a different code for "${conflictItem?.name}".`, 'error');
      return;
    }

    setSubmitting(true);
    try {
      let grnNumber = editingGRN?.grnNumber;
      if (!editingGRN) {
        const gen = await generateGRNNumber();
        grnNumber = gen.grnNumber;
      }

      const processedItems = items.map((item, index) => {
        const qty = parseFloat(item.qtyReceived) || 0;
        const cost = parseFloat(item.costPerUnit) || 0;
        return {
          itemIndex: index,
          name: item.name.trim(),
          category: item.category.trim() || 'Uncategorized',
          itemCode: item.itemCode?.trim() || '',
          qtyReceived: qty,
          costPerUnit: cost,
          totalCost: parseFloat((qty * cost).toFixed(2)),
          isInternal: !!item.isInternal,
          // store qty tracks what's still in store (not yet transferred)
          storeQty: editingGRN ? (item.storeQty ?? qty) : qty,
          transferredQty: item.transferredQty || 0,
        };
      });

      const grnData = {
        grnNumber,
        supplier: supplier.trim(),
        invoiceNumber: invoiceNumber.trim(),
        date,
        notes: notes.trim(),
        items: processedItems,
        status: editingGRN?.status || 'Received',
        grandTotal: parseFloat(grandTotal.toFixed(2)),
        totalQtyReceived: processedItems.reduce((s, i) => s + i.qtyReceived, 0),
        ...(selectedPO && { poNumber: selectedPO.poNumber, poId: selectedPO.id }),
      };

      if (editingGRN) {
        const result = await updateGRN(editingGRN.id, grnData);
        if (result.success) {
          setGRNs((prev) => prev.map((g) => g.id === editingGRN.id ? { ...g, ...grnData } : g));
          closeModal();
          showSnackbar('GRN updated');
          logActivity({
            userId: currentUser?.uid, userEmail: currentUser?.email,
            userName: currentUser?.displayName || currentUser?.email,
            action: ACTIONS.UPDATED_GRN,
            entityType: ENTITY_TYPES.GRN,
            entityId: editingGRN.id, entityName: `GRN #${grnNumber}`,
            details: { supplier: supplier.trim(), invoiceNumber: invoiceNumber.trim(), itemCount: processedItems.length },
          });
        } else showSnackbar(result.error || 'Failed to update GRN', 'error');
      } else {
        const result = await addGRN(grnData);
        if (result.success) {
          // Link to Purchase Order if one was selected
          if (selectedPO) {
            linkGRNtoPO(selectedPO.id, {
              grnId: result.id,
              grnNumber: grnNumber,
              receivedQuantity: processedItems.reduce((s, i) => s + i.qtyReceived, 0),
            }, currentUser).catch(() => {});
          }

          // Create Trade Payable — money owed to supplier on receipt of goods
          if (supplier.trim() && grnData.grandTotal > 0) {
            const dueDate = new Date(date);
            dueDate.setDate(dueDate.getDate() + 30);
            // Find real supplier doc ID — prefer PO's supplierId, then look up by name
            const matchedSupplier = suppliers.find(s => s.name.toLowerCase() === supplier.trim().toLowerCase());
            const resolvedSupplierId = selectedPO?.supplierId || matchedSupplier?.id || `supplier_${supplier.trim().toLowerCase().replace(/\s+/g, '_')}`;
            createTradePayable({
              supplierId: resolvedSupplierId,
              supplierName: supplier.trim(),
              grnId: result.id,
              grnNumber: grnNumber,
              invoiceNumber: invoiceNumber.trim(),
              totalAmount: grnData.grandTotal,
              dueDate,
              paymentTerms: 'Net 30',
            }, currentUser).catch(() => {});
          }
          setGRNs((prev) => [{ id: result.id, ...grnData, createdAt: new Date().toISOString() }, ...prev]);
          closeModal();
          showSnackbar(`GRN ${grnNumber} created`);
          logActivity({
            userId: currentUser?.uid, userEmail: currentUser?.email,
            userName: currentUser?.displayName || currentUser?.email,
            action: ACTIONS.CREATED_GRN,
            entityType: ENTITY_TYPES.GRN,
            entityId: result.id, entityName: `GRN #${grnNumber}`,
            details: { supplier: supplier.trim(), invoiceNumber: invoiceNumber.trim(), itemCount: processedItems.length },
          });
        } else showSnackbar(result.error || 'Failed to create GRN', 'error');
      }
    } catch (err) {
      showSnackbar('Unexpected error', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (grn) => {
    if (grn.status !== 'Received') {
      showSnackbar('Cannot delete a GRN that has already been transferred', 'error');
      setDeleteConfirm(null);
      return;
    }
    setSubmitting(true);
    const result = await deleteGRN(grn.id);
    if (result.success) {
      // Also delete the linked trade payable (if any) — GRN was never transferred so no payment should have been made
      deletePayableByGRN(grn.id).catch(() => {});
      setGRNs((prev) => prev.filter((g) => g.id !== grn.id));
      showSnackbar('GRN deleted');
      logActivity({
        userId: currentUser?.uid, userEmail: currentUser?.email,
        userName: currentUser?.displayName || currentUser?.email,
        action: ACTIONS.DELETED_GRN,
        entityType: ENTITY_TYPES.GRN,
        entityId: grn.id, entityName: `GRN #${grn.grnNumber}`,
      });
    } else showSnackbar(result.error || 'Failed to delete', 'error');
    setSubmitting(false);
    setDeleteConfirm(null);
  };

  const statusColor = (s) =>
    s === 'Completed' ? 'success' : s === 'Partial' ? 'warning' : 'info';

  const filtered = grns.filter(
    (g) =>
      (g.grnNumber || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (g.supplier || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (g.invoiceNumber || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width={280} height={48} sx={{ mb: 1 }} />
        <Skeleton variant="text" width={400} height={28} sx={{ mb: 3 }} />
        <Skeleton variant="rectangular" width="100%" height={400} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Goods Received Notes
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Record stock received from suppliers. Use Stock Transfer to move items to the shop.
        </Typography>
      </Box>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h6" fontWeight={600}>All GRNs</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="outlined" size="small"
                startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshCw size={16} />}
                onClick={handleRefresh} disabled={refreshing}>
                Refresh
              </Button>
              <Button variant="contained" size="small" startIcon={<Plus size={16} />} onClick={openCreate}>
                New GRN
              </Button>
            </Box>
          </Box>

          <TextField fullWidth placeholder="Search by GRN #, supplier or invoice #..."
            value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} sx={{ mb: 3 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon size={18} /></InputAdornment> }}
          />

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>GRN #</strong></TableCell>
                  <TableCell><strong>PO #</strong></TableCell>
                  <TableCell><strong>Supplier</strong></TableCell>
                  <TableCell><strong>Invoice #</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell align="center"><strong>Items</strong></TableCell>
                  <TableCell align="right"><strong>Grand Total</strong></TableCell>
                  <TableCell align="center"><strong>Status</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((grn) => (
                  <TableRow key={grn.id} hover>
                    <TableCell>
                      <Typography variant="body2" color="primary" fontWeight={600}>{grn.grnNumber}</Typography>
                    </TableCell>
                    <TableCell>
                      {grn.poNumber
                        ? <Chip label={grn.poNumber} size="small" variant="outlined" color="secondary" />
                        : <Typography variant="body2" color="text.disabled">—</Typography>}
                    </TableCell>
                    <TableCell>{grn.supplier}</TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{grn.invoiceNumber || '—'}</Typography>
                    </TableCell>
                    <TableCell>{grn.date}</TableCell>
                    <TableCell align="center">{grn.items?.length || 0}</TableCell>
                    <TableCell align="right">LKR {fmt(grn.grandTotal)}</TableCell>
                    <TableCell align="center">
                      <Chip label={grn.status || 'Received'} size="small" color={statusColor(grn.status)} />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View / Edit">
                        <IconButton size="small" color="primary" onClick={() => openEdit(grn)}>
                          <Eye size={17} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={!canDelete ? 'No permission to delete' : grn.status !== 'Received' ? 'Cannot delete — already transferred' : 'Delete'}>
                        <span>
                          <IconButton size="small" color="error"
                            disabled={!canDelete || grn.status !== 'Received'}
                            onClick={() => setDeleteConfirm(grn)}>
                            <Trash2 size={17} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                      <Package size={40} style={{ opacity: 0.25, display: 'block', margin: '0 auto 8px' }} />
                      <Typography color="text.secondary">No GRNs found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* ── Create / Edit Dialog ── */}
      <Dialog open={showModal} onClose={closeModal} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          {editingGRN ? `Edit GRN — ${editingGRN.grnNumber}` : 'New Goods Received Note'}
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {/* Header fields */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <Autocomplete
                    freeSolo
                    fullWidth
                    options={suppliers.map(s => s.name)}
                    value={supplier}
                    onInputChange={(_, val) => setSupplier(val)}
                    renderInput={(params) => (
                      <TextField {...params} label="Supplier *" placeholder="Select or type supplier name" />
                    )}
                  />
                  <Tooltip title="Quick-add new supplier">
                    <IconButton
                      onClick={() => { setQuickSupplierName(supplier); setShowQuickSupplier(true); }}
                      sx={{ mt: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                    >
                      <Plus size={18} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Invoice Number *" value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)} placeholder="Supplier's invoice #" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  fullWidth 
                  label="Date" 
                  type="date" 
                  value={date}
                  onChange={(e) => setDate(e.target.value)} 
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Notes" value={notes}
                  onChange={(e) => setNotes(e.target.value)} placeholder="Optional notes" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={pendingPOs}
                  getOptionLabel={(po) => `${po.poNumber} — ${po.supplierName}`}
                  value={selectedPO}
                  onChange={(_, val) => {
                    setSelectedPO(val);
                    if (val) {
                      setSupplier(val.supplierName);
                      // Pre-fill items from PO
                      if (val.items && val.items.length > 0) {
                        setItems(val.items.map((item) => ({
                          name: item.productName || item.name || '',
                          category: item.category || '',
                          itemCode: item.itemCode || '',
                          qtyReceived: String(item.quantity || ''),
                          costPerUnit: String(item.costPrice || ''),
                        })));
                      }
                    }
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Link to Purchase Order (optional)"
                      placeholder="Select a pending PO" />
                  )}
                  noOptionsText="No pending purchase orders"
                />
              </Grid>
            </Grid>

            {/* Items table */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="subtitle1" fontWeight={600}>Items Received</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Download Excel template">
                  <Button size="small" variant="outlined" startIcon={<Download size={14} />} onClick={downloadTemplate}>
                    Template
                  </Button>
                </Tooltip>
                <Tooltip title="Import items from Excel / CSV">
                  <Button size="small" variant="outlined" color="secondary"
                    startIcon={<Upload size={14} />}
                    onClick={() => fileInputRef.current?.click()}>
                    Import
                  </Button>
                </Tooltip>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  style={{ display: 'none' }}
                  onChange={handleFileUpload}
                />
                <Button size="small" variant="outlined" startIcon={<Plus size={14} />} onClick={addItem}>
                  Add Row
                </Button>
              </Box>
            </Box>

            <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 600, whiteSpace: 'nowrap' } }}>
                    <TableCell>Item Name *</TableCell>                    <TableCell>Item Code</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell align="right">Qty Received *</TableCell>
                    <TableCell align="right">Cost / Unit (LKR) *</TableCell>
                    <TableCell align="right">Total Cost</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item, index) => {
                    const qty = parseFloat(item.qtyReceived) || 0;
                    const cost = parseFloat(item.costPerUnit) || 0;
                    const total = qty * cost;
                    return (
                      <TableRow key={index}>
                        <TableCell>
                          <Autocomplete
                            freeSolo
                            options={existingItemNames}
                            value={item.name}
                            onInputChange={(_, val) => updateItem(index, 'name', val)}
                            onChange={(_, val) => {
                              if (val) {
                                // Auto-fill category — check inventory first, then past GRNs
                                const fromInventory = inventory.find(
                                  (i) => i.name.toLowerCase() === val.toLowerCase()
                                );
                                const fromGRN = !fromInventory
                                  ? grns.flatMap((g) => g.items || []).find(
                                      (i) => i.name.toLowerCase() === val.toLowerCase()
                                    )
                                  : null;
                                updateItem(index, 'name', val);
                                const cat = fromInventory?.category || fromGRN?.category;
                                if (cat) updateItem(index, 'category', cat);
                                const code = fromInventory?.itemCode || fromGRN?.itemCode;
                                if (code) updateItem(index, 'itemCode', code);
                              }
                            }}
                            renderInput={(params) => (
                              <TextField {...params} size="small" placeholder="Item name"
                                sx={{ minWidth: 180 }} />
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <Autocomplete
                            freeSolo
                            options={existingItemCodes}
                            getOptionLabel={(option) =>
                              typeof option === 'string' ? option : option.code
                            }
                            renderOption={(props, option) => (
                              <li {...props} key={option.code}>
                                <Box>
                                  <Typography variant="body2" fontWeight={600}>{option.code}</Typography>
                                  <Typography variant="caption" color="text.secondary">{option.name}</Typography>
                                </Box>
                              </li>
                            )}
                            value={item.itemCode || ''}
                            onInputChange={(_, val) => updateItem(index, 'itemCode', val)}
                            onChange={(_, val) => {
                              if (val && typeof val === 'object') {
                                // Auto-fill name and category when item code is selected
                                updateItem(index, 'itemCode', val.code);
                                if (val.name) updateItem(index, 'name', val.name);
                                if (val.category) updateItem(index, 'category', val.category);
                              }
                            }}
                            renderInput={(params) => (
                              <TextField {...params} size="small" placeholder="e.g. PRF-001"
                                sx={{ width: 130 }} />
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <Autocomplete
                            freeSolo
                            options={existingCategories}
                            value={item.category}
                            onInputChange={(_, val) => updateItem(index, 'category', val)}
                            renderInput={(params) => (
                              <TextField {...params} size="small" placeholder="e.g. Perfume"
                                sx={{ minWidth: 130 }} />
                            )}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <TextField type="number" size="small" value={item.qtyReceived}
                            onChange={(e) => updateItem(index, 'qtyReceived', e.target.value)}
                            sx={{ width: 80 }} inputProps={{ min: 0 }} />
                        </TableCell>
                        <TableCell align="right">
                          <TextField type="number" size="small" value={item.costPerUnit}
                            onChange={(e) => updateItem(index, 'costPerUnit', e.target.value)}
                            sx={{ width: 100 }} inputProps={{ min: 0, step: 0.01 }} />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={500}>
                            {total > 0 ? `LKR ${fmt(total)}` : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" color="error" onClick={() => removeItem(index)}
                            disabled={items.length === 1}>
                            <Trash2 size={15} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Grand total */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, pr: 1 }}>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="caption" color="text.secondary">Grand Total</Typography>
                <Typography variant="h6" fontWeight={700} color="primary">
                  LKR {fmt(grandTotal)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeModal} disabled={submitting}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={submitting}
            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}>
            {submitting ? 'Saving...' : editingGRN ? 'Update GRN' : 'Create GRN'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Import Preview Dialog ── */}
      <Dialog open={showImportDialog} onClose={() => setShowImportDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Import Preview</DialogTitle>
        <Divider />
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {importErrors.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  {importErrors.length} row(s) skipped due to errors:
                </Typography>
                {importErrors.map((e, i) => (
                  <Typography key={i} variant="caption" display="block">{e}</Typography>
                ))}
              </Alert>
            )}

            {importPreview.length > 0 ? (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {importPreview.length} item(s) ready to import. Review below then click Confirm.
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>#</strong></TableCell>
                        <TableCell><strong>Item Name</strong></TableCell>
                        <TableCell><strong>Category</strong></TableCell>
                        <TableCell align="right"><strong>Qty Received</strong></TableCell>
                        <TableCell align="right"><strong>Cost / Unit</strong></TableCell>
                        <TableCell align="right"><strong>Total</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {importPreview.map((row, i) => (
                        <TableRow key={i}>
                          <TableCell>{i + 1}</TableCell>
                          <TableCell>{row.name}</TableCell>
                          <TableCell>
                            <Chip label={row.category} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell align="right">{row.qtyReceived}</TableCell>
                          <TableCell align="right">LKR {fmt(row.costPerUnit)}</TableCell>
                          <TableCell align="right">LKR {fmt(row.qtyReceived * row.costPerUnit)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Grand Total:&nbsp;
                    <strong>
                      LKR {fmt(importPreview.reduce((s, r) => s + r.qtyReceived * r.costPerUnit, 0))}
                    </strong>
                  </Typography>
                </Box>
              </>
            ) : (
              <Alert severity="error">No valid rows found in the file.</Alert>
            )}
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setShowImportDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={applyImport} disabled={importPreview.length === 0}
            startIcon={<Upload size={16} />}>
            Confirm Import
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirm ── */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete GRN?</DialogTitle>
        <DialogContent>
          <Typography>
            Delete <strong>{deleteConfirm?.grnNumber}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteConfirm(null)} disabled={submitting}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => handleDelete(deleteConfirm)}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Quick-add Supplier Dialog */}
      <Dialog open={showQuickSupplier} onClose={() => setShowQuickSupplier(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Quick Add Supplier</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Supplier Name *"
              value={quickSupplierName}
              onChange={e => setQuickSupplierName(e.target.value)}
              fullWidth
              autoFocus
            />
            <TextField
              label="Phone"
              value={quickSupplierPhone}
              onChange={e => setQuickSupplierPhone(e.target.value)}
              fullWidth
            />
            <TextField
              label="Email"
              value={quickSupplierEmail}
              onChange={e => setQuickSupplierEmail(e.target.value)}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setShowQuickSupplier(false)} disabled={quickSupplierSubmitting}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleQuickAddSupplier}
            disabled={quickSupplierSubmitting || !quickSupplierName.trim()}
            startIcon={quickSupplierSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {quickSupplierSubmitting ? 'Adding...' : 'Add Supplier'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GRN;
